import React, { useState, useEffect } from 'react';
import { apiRequest, uploadWithProgress } from '../utils/api';
import Layout from '../components/Layout';

interface UpdateInfo {
  version: string;
  releaseDate: string;
  changelog: {
    features?: string[];
    improvements?: string[];
    bugfixes?: string[];
    breaking?: string[];
  };
  minVersion?: string;
  critical?: boolean;
}

interface Version {
  appType: string;
  version: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  hasUpdateInfo: boolean;
  updateInfo?: UpdateInfo;
}

const AppVersions: React.FC = () => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingVersion, setEditingVersion] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdateInfo | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [appType, setAppType] = useState('');



  useEffect(() => {
    fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVersions = async () => {
    try {
      const response = await apiRequest('/api/app-update/versions');
      const data = await response.json();
      setVersions(data.data || []);
    } catch (error: any) {
      showNotification('error', error.message || 'Failed to fetch versions');
      setVersions([]);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Prevent duplicate upload
    if (uploading) {
      console.log('Upload already in progress, ignoring duplicate call');
      return;
    }

    // Validate filename format (must contain -V{version}.zip or _V{version}.zip or _v{version}.zip)
    const match = file.name.match(/[-_][Vv]([0-9]+(?:\.[0-9]+)*)\.zip$/i);
    if (!match) {
      showNotification('error', 'Invalid filename format. Expected: anything-V{version}.zip or anything_v{version}.zip (e.g., Bot-Support-V1.0.0.zip or BotSupport_v1.0.0.2.zip)');
      e.target.value = '';
      return;
    }

    // Show modal to input appType
    setSelectedFile(file);
    setAppType('');
    setShowUploadModal(true);
    e.target.value = ''; // Reset input
  };

  const handleUploadConfirm = async () => {
    if (!selectedFile || !appType.trim()) {
      showNotification('error', 'Please enter app type');
      return;
    }

    // Validate appType format
    if (!/^[a-zA-Z0-9_-]+$/.test(appType)) {
      showNotification('error', 'App type can only contain letters, numbers, underscore, and hyphen');
      return;
    }

    // Extract version from filename (support both -V and _v)
    const match = selectedFile.name.match(/[-_][Vv]([0-9]+(?:\.[0-9]+)*)\.zip$/i);
    const extractedVersion = match ? match[1] : '';

    setShowUploadModal(false);
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('appType', appType.trim());

    try {
      const uploadResult = await uploadWithProgress(
        '/api/app-update/upload',
        formData,
        (percent) => setUploadProgress(percent)
      );

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Upload failed');
      }

      setUploading(false);
      setUploadProgress(0);
      setSelectedFile(null);
      setAppType('');

      // Refresh versions list
      await fetchVersions();

      // File uploaded successfully, now open edit form for changelog
      setEditingVersion(`${appType.trim()}:${extractedVersion}`);
      setEditForm({
        version: extractedVersion,
        releaseDate: new Date().toISOString().split('T')[0],
        changelog: {
          features: [],
          improvements: [],
          bugfixes: [],
          breaking: []
        },
        critical: false
      });

      showNotification('success', `File uploaded successfully! Now add changelog for ${appType.trim()} version ${extractedVersion}`);
    } catch (error: any) {
      showNotification('error', error.message || 'Upload failed');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleEdit = (version: Version) => {
    setEditingVersion(`${version.appType}:${version.version}`); // Store appType:version
    setEditForm(version.updateInfo || {
      version: version.version,
      releaseDate: new Date().toISOString().split('T')[0],
      changelog: {
        features: [],
        improvements: [],
        bugfixes: []
      }
    });
  };

  const handleSave = async () => {
    if (!editingVersion || !editForm) return;

    const [appType, version] = editingVersion.split(':');

    try {
      // Just update the changelog info (file already uploaded in handleFileSelect)
      const response = await apiRequest(`/api/app-update/versions/${appType}/${version}`, {
        method: 'PUT',
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Update failed');
      }

      showNotification('success', `✅ Version ${version} for ${appType} changelog saved successfully!`);
      setEditingVersion(null);
      setEditForm(null);
      fetchVersions();
    } catch (error: any) {
      showNotification('error', 'Save failed: ' + error.message);
    }
  };

  const handleDelete = async (appType: string, version: string) => {
    if (!window.confirm(`Are you sure you want to delete ${appType} version ${version}?`)) return;

    try {
      const response = await apiRequest(`/api/app-update/versions/${appType}/${version}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Delete failed');
      }

      showNotification('success', 'Version deleted successfully');
      fetchVersions();
    } catch (error: any) {
      showNotification('error', error.message || 'Delete failed');
    }
  };

  const addChangelogItem = (category: 'features' | 'improvements' | 'bugfixes' | 'breaking') => {
    if (!editForm) return;
    const newItem = prompt(`Add new ${category} item:`);
    if (!newItem) return;

    setEditForm({
      ...editForm,
      changelog: {
        ...editForm.changelog,
        [category]: [...(editForm.changelog[category] || []), newItem]
      }
    });
  };

  const removeChangelogItem = (category: 'features' | 'improvements' | 'bugfixes' | 'breaking', index: number) => {
    if (!editForm) return;
    setEditForm({
      ...editForm,
      changelog: {
        ...editForm.changelog,
        [category]: editForm.changelog[category]?.filter((_, i) => i !== index)
      }
    });
  };

  const renderEditForm = () => {
    if (!editForm) return null;

    const changelogCategories = [
      { key: 'features' as const, label: '✨ Features', color: '#3b82f6', placeholder: 'e.g., Added auto-update feature' },
      { key: 'improvements' as const, label: '🚀 Improvements', color: '#10b981', placeholder: 'e.g., 50% faster connection speed' },
      { key: 'bugfixes' as const, label: '🐛 Bugfixes', color: '#f59e0b', placeholder: 'e.g., Fixed crash on startup' },
      { key: 'breaking' as const, label: '⚠️ Breaking Changes', color: '#ef4444', placeholder: 'e.g., Removed old API v1 support' }
    ];

    return (
      <div style={{ display: 'grid', gap: '20px' }}>
        {/* Version Number - Read only */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
            🔢 Version Number
          </label>
          <input
            type="text"
            value={editForm.version}
            disabled
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: '#f3f4f6',
              cursor: 'not-allowed'
            }}
          />
        </div>

        {/* Release Date */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
            📅 Release Date
          </label>
          <input
            type="date"
            value={editForm.releaseDate}
            onChange={(e) => setEditForm({ ...editForm, releaseDate: e.target.value })}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        {/* Changelog Categories */}
        {changelogCategories.map(({ key, label, color, placeholder }) => (
          <div key={key}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ fontWeight: 500, fontSize: '14px', color }}>
                {label}
              </label>
              <button
                onClick={() => addChangelogItem(key)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: color,
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 500
                }}
              >
                + Add
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {editForm.changelog[key]?.map((item, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => {
                      const newItems = [...(editForm.changelog[key] || [])];
                      newItems[index] = e.target.value;
                      setEditForm({
                        ...editForm,
                        changelog: { ...editForm.changelog, [key]: newItems }
                      });
                    }}
                    placeholder={placeholder}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    onClick={() => removeChangelogItem(key, index)}
                    style={{
                      padding: '10px 14px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    🗑️
                  </button>
                </div>
              ))}
              {(!editForm.changelog[key] || editForm.changelog[key]?.length === 0) && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#6b7280',
                  textAlign: 'center'
                }}>
                  No {label.toLowerCase()} added yet. Click "+ Add" to add items.
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Additional Options */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
              📦 Minimum Version (optional)
            </label>
            <input
              type="text"
              value={editForm.minVersion || ''}
              onChange={(e) => setEditForm({ ...editForm, minVersion: e.target.value })}
              placeholder="e.g., 1.0.0"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '32px' }}>
              <input
                type="checkbox"
                checked={editForm.critical || false}
                onChange={(e) => setEditForm({ ...editForm, critical: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 500, fontSize: '14px' }}>
                ⚠️ Critical Update (Force update)
              </span>
            </label>
          </div>
        </div>
      </div>
    );
  };

  const formatFileSize = (bytes: number) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  if (loading) {
    return <div style={{ padding: '24px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <Layout>
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>🔄 App Versions Management</h1>
        
        <label style={{
          padding: '10px 20px',
          backgroundColor: uploading ? '#9ca3af' : '#3b82f6',
          color: 'white',
          borderRadius: '6px',
          cursor: uploading ? 'not-allowed' : 'pointer',
          fontWeight: 500
        }}>
          {uploading ? '⏳ Uploading...' : '📤 Upload New Version'}
          <input
            type="file"
            accept=".zip"
            onChange={handleFileSelect}
            disabled={uploading}
            style={{ display: 'none' }}
          />
        </label>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600 }}>
              📦 Upload Application
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
              File: <strong>{selectedFile?.name}</strong>
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
                App Type <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={appType}
                onChange={(e) => setAppType(e.target.value)}
                placeholder="e.g., whatsappbot, smmapp, orderbot"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                autoFocus
              />
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                Only letters, numbers, underscore (_), and hyphen (-) allowed
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  setAppType('');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadConfirm}
                disabled={!appType.trim()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: appType.trim() ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: appType.trim() ? 'pointer' : 'not-allowed',
                  fontWeight: 500
                }}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          backgroundColor: notification.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: notification.type === 'success' ? '#065f46' : '#991b1b',
          borderRadius: '6px',
          border: `1px solid ${notification.type === 'success' ? '#10b981' : '#ef4444'}`
        }}>
          {notification.message}
        </div>
      )}

      {/* Upload Progress Bar */}
      {uploading && (
        <div style={{
          marginBottom: '16px',
          padding: '16px',
          backgroundColor: '#f3f4f6',
          borderRadius: '8px',
          border: '1px solid #d1d5db'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
              📤 Uploading file...
            </span>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#3b82f6' }}>
              {uploadProgress}%
            </span>
          </div>

          {/* Progress Bar */}
          <div style={{
            width: '100%',
            height: '24px',
            backgroundColor: '#e5e7eb',
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              width: `${uploadProgress}%`,
              height: '100%',
              backgroundColor: uploadProgress === 100 ? '#10b981' : '#3b82f6',
              transition: 'width 0.3s ease, background-color 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '12px',
              fontWeight: 600
            }}>
              {uploadProgress > 10 && `${uploadProgress}%`}
            </div>
          </div>

          {/* File size info */}
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
            {uploadProgress < 100 ? 'Đang tải lên...' : '✅ Upload hoàn tất! Đang xử lý...'}
          </div>
        </div>
      )}

      {/* Instructions - Only show when not editing */}
      {!editingVersion && (
        <div style={{
          padding: '20px',
          backgroundColor: '#eff6ff',
          borderRadius: '8px',
          marginBottom: '24px',
          fontSize: '14px',
          border: '1px solid #3b82f6'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#1e40af' }}>
            📝 Hướng dẫn sử dụng
          </div>

          <div style={{ marginBottom: '16px' }}>
            <strong>1️⃣ Chuẩn bị file ZIP:</strong>
            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
              <li>Format tên file: <code style={{ backgroundColor: '#dbeafe', padding: '2px 6px', borderRadius: '4px' }}>anything-V1.0.1.zip</code> hoặc <code style={{ backgroundColor: '#dbeafe', padding: '2px 6px', borderRadius: '4px' }}>anything_v1.0.1.zip</code></li>
              <li>Ví dụ: Bot-Support-V1.0.1.zip, BotSupport_v1.0.0.2.zip</li>
              <li>Phần <code style={{ backgroundColor: '#dbeafe', padding: '2px 6px', borderRadius: '4px' }}>-V1.0.1</code> hoặc <code style={{ backgroundColor: '#dbeafe', padding: '2px 6px', borderRadius: '4px' }}>_v1.0.1</code> là bắt buộc để hệ thống nhận diện version</li>
              <li>File ZIP chứa toàn bộ ứng dụng của bạn</li>
            </ul>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <strong>2️⃣ Upload và chọn App Type:</strong>
            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
              <li>Click nút <strong>"📤 Upload New Version"</strong></li>
              <li>Chọn file ZIP của bạn</li>
              <li>Nhập <strong>App Type</strong> (ví dụ: whatsappbot, smmapp, orderbot)</li>
              <li>File sẽ được lưu với tên: <code style={{ backgroundColor: '#dbeafe', padding: '2px 6px', borderRadius: '4px' }}>{'{appType}'}-V{'{version}'}.zip</code></li>
            </ul>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <strong>3️⃣ Thêm Changelog:</strong>
            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
              <li>Sau khi upload, form sẽ tự động mở để nhập thông tin</li>
              <li>Điền thông tin: Features, Improvements, Bugfixes, Breaking Changes</li>
              <li>Changelog sẽ được lưu vào file JSON riêng</li>
              <li>Click <strong>"💾 Save"</strong> để lưu thay đổi</li>
            </ul>
          </div>

          <div>
            <strong>4️⃣ Client check update:</strong>
            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
              <li>API: <code style={{ backgroundColor: '#dbeafe', padding: '2px 6px', borderRadius: '4px' }}>GET /api/app-update/check/{'{appType}'}</code></li>
              <li>Trả về version mới nhất của app type đó</li>
              <li>Download URL với signed token (expires 5 phút)</li>
              <li>Changelog đầy đủ và các thông tin khác</li>
            </ul>
          </div>
        </div>
      )}

      {/* Edit Form - Show at top when editing */}
      {editingVersion && editForm && (
        <div style={{
          backgroundColor: '#fef3c7',
          border: '2px solid #f59e0b',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#92400e' }}>
              ✏️ Chỉnh sửa thông tin Version {editingVersion}
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSave}
                disabled={uploading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: uploading ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  fontSize: '14px'
                }}
              >
                💾 Save Changes
              </button>
              <button
                onClick={() => {
                  setEditingVersion(null);
                  setEditForm(null);
                }}
                disabled={uploading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  fontSize: '14px'
                }}
              >
                ❌ Cancel
              </button>
            </div>
          </div>

          {/* Form fields will be rendered here */}
          {renderEditForm()}
        </div>
      )}

      {/* Versions List */}
      {versions.length === 0 ? (
        <div style={{
          padding: '48px',
          textAlign: 'center',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '2px dashed #d1d5db'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
          <div style={{ fontSize: '18px', color: '#6b7280', marginBottom: '8px' }}>No versions uploaded yet</div>
          <div style={{ fontSize: '14px', color: '#9ca3af' }}>Upload your first version to get started</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {versions.map((version) => (
            <div key={`${version.appType}-${version.version}`} style={{
              backgroundColor: 'white',
              border: editingVersion === `${version.appType}:${version.version}` ? '2px solid #f59e0b' : '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '20px',
              boxShadow: editingVersion === `${version.appType}:${version.version}` ? '0 4px 6px rgba(245, 158, 11, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
              opacity: editingVersion && editingVersion !== `${version.appType}:${version.version}` ? 0.6 : 1
            }}>
              {/* View Mode */}
              <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 600 }}>
                        📦 {version.appType} - Version {version.version}
                        {versions[0].appType === version.appType && versions[0].version === version.version && (
                          <span style={{
                            marginLeft: '12px',
                            padding: '4px 12px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 500
                          }}>
                            LATEST
                          </span>
                        )}
                      </h3>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        <div>🏷️ App: <strong>{version.appType}</strong></div>
                        <div>📁 {version.fileName}</div>
                        <div>💾 {formatFileSize(version.fileSize)}</div>
                        <div>📅 Uploaded: {new Date(version.uploadedAt).toLocaleString()}</div>
                        {version.updateInfo?.releaseDate && (
                          <div>🚀 Release: {new Date(version.updateInfo.releaseDate).toLocaleDateString()}</div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEdit(version)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 500
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(version.appType, version.version)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 500
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>

                  {/* Changelog Display */}
                  {version.updateInfo && (
                    <div style={{
                      marginTop: '16px',
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600 }}>📝 Changelog</h4>

                      {version.updateInfo.critical && (
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: '#fee2e2',
                          color: '#991b1b',
                          borderRadius: '4px',
                          marginBottom: '12px',
                          fontSize: '14px',
                          fontWeight: 500
                        }}>
                          ⚠️ Critical Update - Force update required
                        </div>
                      )}

                      {version.updateInfo.minVersion && (
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                          borderRadius: '4px',
                          marginBottom: '12px',
                          fontSize: '14px'
                        }}>
                          📌 Minimum version required: {version.updateInfo.minVersion}
                        </div>
                      )}

                      {(['features', 'improvements', 'bugfixes', 'breaking'] as const).map((category) => {
                        const items = version.updateInfo?.changelog[category];
                        if (!items || items.length === 0) return null;

                        return (
                          <div key={category} style={{ marginBottom: '12px' }}>
                            <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '14px', textTransform: 'capitalize' }}>
                              {category === 'features' && '✨'}
                              {category === 'improvements' && '🚀'}
                              {category === 'bugfixes' && '🐛'}
                              {category === 'breaking' && '⚠️'}
                              {' '}{category}:
                            </div>
                            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '14px' }}>
                              {items.map((item, index) => (
                                <li key={index} style={{ marginBottom: '4px' }}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}

                      {!version.updateInfo.changelog.features?.length &&
                       !version.updateInfo.changelog.improvements?.length &&
                       !version.updateInfo.changelog.bugfixes?.length &&
                       !version.updateInfo.changelog.breaking?.length && (
                        <div style={{ color: '#9ca3af', fontSize: '14px', fontStyle: 'italic' }}>
                          No changelog added yet. Click "Edit" to add release notes.
                        </div>
                      )}
                    </div>
                  )}

                  {!version.hasUpdateInfo && (
                    <div style={{
                      marginTop: '16px',
                      padding: '12px',
                      backgroundColor: '#fef3c7',
                      color: '#92400e',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}>
                      ⚠️ No changelog available. Click "Edit" to add release notes.
                    </div>
                  )}
                </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </Layout>
  );
};

export default AppVersions;

